import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

axios.defaults.baseURL = process.env.REACT_APP_API_URL;

export const getClientType = createAsyncThunk(
  "clientType/getClientTypeList",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post("/clientType/getClientTypeList", data, {
        headers: {
          "Content-Type": "application/json",
          token: data.token,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createClientType = createAsyncThunk(
  "clientType/createClientType",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post("/clientType/createClientType", data, {
        headers: {
          "Content-Type": "application/json",
          token: data.token,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateClientType = createAsyncThunk(
  "clientType/updateClientType",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.put("/clientType/updateClientType", data, {
        headers: {
          "Content-Type": "application/json",
          token: data.token,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteClientType = createAsyncThunk(
  "clientType/deleteClientType",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.put("/clientType/deleteClientType", data, {
        headers: {
          "Content-Type": "application/json",
          token: data.token,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const getClientTypeById = createAsyncThunk(
  "clientType/getClientTypeById",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post("/clientType/getClientTypeById", data, {
        headers: {
          "Content-Type": "application/json",
          token: data.token,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  loading: false,
  clientTypes: [],
  clientTypeInfo: null,
  error: null,
  searchMessage: null,
  isLoading: false,
  createClientTypeSuccessMessage: null,
  createClientTypeErrorMessage: null,
  updateClientTypeSuccessMessage: null,
  updateClientTypeErrorMessage: null,
  deleteClientTypeSuccessMessage: null,
  deleteClientTypeErrorMessage: null,
  currentPage: 1,
  totalPages: null,
  hasMore: true,
  totalCount: 0,
};

const clientTypeSlice = createSlice({
  name: "clientTypes",
  initialState,
  reducers: {
    resetCreateMessages: (state) => {
      state.createClientTypeSuccessMessage = null;
      state.createClientTypeErrorMessage = null;
    },
    resetUpdateMessages: (state) => {
      state.updateClientTypeSuccessMessage = null;
      state.updateClientTypeErrorMessage = null;
    },
    resetDeleteMessages: (state) => {
      state.deleteClientTypeSuccessMessage = null;
      state.deleteClientTypeErrorMessage = null;
    },
    resetClientTypeInfo: (state) => {
      state.clientTypeInfo = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getClientType.pending, (state) => {
        state.loading = true;
      })
      .addCase(getClientType.fulfilled, (state, action) => {
        state.loading = false;
        if (!action.payload.success) {
          state.clientTypes = [];
          state.totalCount = 0;
          state.totalPages = 1;
          state.searchMessage =
            action.payload.result?.message || "No data found";
          return;
        }
        const result = action.payload.result;
        state.clientTypes = result.list || [];
        state.currentPage = Number(result.currentPage) || 1;
        state.totalPages = Number(result.totalPages) || 1;
        state.totalCount = Number(result.totalRecords) || 0;
        state.searchMessage = null;
      })
      .addCase(getClientType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.result?.message;
      })

      // Get Client Type by Id
      .addCase(getClientTypeById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getClientTypeById.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success === false) {
          state.searchMessage = action.payload.result.message;
        } else {
          state.clientTypeInfo = action.payload.result;
        }
      })
      .addCase(getClientTypeById.rejected, (state, action) => {
        state.loading = false;
        state.searchMessage =
          action.payload?.result?.message ||
          "Failed to fetch Client Type by Id";
      })

      // Create Client Type
      .addCase(createClientType.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createClientType.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.success === false) {
          state.createClientTypeErrorMessage = action.payload.result.message;
        } else {
          state.createClientTypeSuccessMessage = action.payload.result.message;
        }
      })
      .addCase(createClientType.rejected, (state, action) => {
        state.isLoading = false;
        state.createClientTypeErrorMessage = action.payload.result.message;
      })

      // Update Client Type
      .addCase(updateClientType.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateClientType.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.success === true) {
          state.updateClientTypeSuccessMessage = action.payload.result.message;
        } else {
          state.updateClientTypeErrorMessage = action.payload.result.message;
        }
      })
      .addCase(updateClientType.rejected, (state, action) => {
        state.isLoading = false;
        state.updateClientTypeErrorMessage = action.payload.result.message;
      })

      // Delete Client Type
      .addCase(deleteClientType.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteClientType.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.success === true) {
          state.deleteClientTypeSuccessMessage = action.payload.result.message;
        } else {
          state.deleteClientTypeErrorMessage = action.payload.result.message;
        }
      })
      .addCase(deleteClientType.rejected, (state, action) => {
        state.isLoading = false;
        state.deleteClientTypeErrorMessage = action.payload.result.message;
      });
  },
});

export const {
  resetCreateMessages,
  resetUpdateMessages,
  resetDeleteMessages,
  resetClientTypeInfo,
} = clientTypeSlice.actions;
export default clientTypeSlice.reducer;
